/** @format */
import {useState} from 'react';

import {LinearProgress} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import WizardSidebar from 'components/MediaplanSidebar/WizardSidebar';
import RestrictionBlock from 'components/RestrictionBlock';

import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {isLimitReachedError} from 'helpers/apolloErrorHelpers';

import NameStep from '../../NameStep';
import WizardHeader from '../../WizardHeader';
import {
  useCreateCompanyMutation,
  useGetCurrentUserRestrictionsQuery,
} from './graphql/__generated__';

const WizardNew = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();

  const {mixpanel} = useMixpanelContext();
  const {switchCompany} = useCompanyContext();

  const [projectName, setProjectName] = useState('');
  const [saveCompanyError, setSaveCompanyError] = useState('');

  const [createCompany, {loading: createCompanyLoading}] = useCreateCompanyMutation();

  const {data: restrictionsData, loading: restrictionsLoading} =
    useGetCurrentUserRestrictionsQuery();

  const isRestricted =
    restrictionsData?.currentUser?.restrictions.projectCreationRestricted === true;

  if (createCompanyLoading || restrictionsLoading) return <LinearProgress style={{flex: 1}} />;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProjectName(event.target.value);
    if (saveCompanyError) setSaveCompanyError('');
  };

  const handleProjectNameConfirm = async () => {
    setSaveCompanyError('');

    try {
      mixpanel.track('click_wizard_create_company_button');

      const {data: createCompanyData} = await createCompany({
        variables: {name: projectName},
      });

      if (!createCompanyData?.createCompany.id) {
        setSaveCompanyError(t('Не удалось создать проект.'));
        return;
      }

      switchCompany(createCompanyData.createCompany);

      navigate(`/home/project/wizard/created?companyId=${createCompanyData.createCompany.id}`);
    } catch (e) {
      if (isLimitReachedError(e)) {
        setSaveCompanyError(t('Достигнут лимит проектов. Обратитесь к менеджеру'));
      } else {
        const message = e instanceof Error ? e.message : 'Unknown error.';
        setSaveCompanyError(message);
      }
    }
  };

  return (
    <>
      <WizardSidebar activeStep={'CREATE'} />

      <WizardHeader />

      {isRestricted ? (
        <RestrictionBlock sx={{mt: 4}} />
      ) : (
        <NameStep
          onChange={handleChange}
          saveCompanyError={saveCompanyError}
          onConfirm={handleProjectNameConfirm}
          projectName={projectName}
        />
      )}
    </>
  );
};

export default WizardNew;
